import { IDMSElement } from "Core/Interfaces";

import { PageContext } from "./PageContext";
import { MappingDisplayTypes } from "Service/Client";

/**
 * Reloads all grids on the current page.
 * @param reloadFilters Pass true if the filter should be reloaded aswell.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * DMS.reloadGrids();
 *
 * // reload filter aswell
 * DMS.reloadGrids(true);
 * ```
 */
export function reloadGrids(reloadFilters: boolean = false): void {
    PageContext.reloadGrids(reloadFilters);
}

/**
 * Hides all grids on the current page.
 *
 * @attention Filters stay visible.
 * @param clearGrids Pass true if the grids should be cleared aswell.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * DMS.hideGrids();
 * DMS.hideGrids(true);
 * ```
 */
export function hideGrids(clearGrids: boolean = true): void {
    PageContext.hideGrids(clearGrids);
}

/**
 * Initialize a mapping in given specific jQuery-container.
 * @param container The jQuery-container in which the mapping will be initialized.
 * @param mappingName Name of the mapping to be initialized. The TextBlocks for the mapping are loaded.
 * @param displayType Specify if both filters and grids should be shown or just one of them.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * var container = $(".container");
 * DMS.initializeMappingInContainer(container, "sec_User");
 *
 * // only grids
 * var container = $(".container");
 * DMS.initializeMappingInContainer(container, "sec_User", DMS.Enums.MappingDisplayTypes.GridsOnly);
 * ```
 */
export async function initializeMappingInContainer(
    container: JQuery,
    mappingName: string,
    displayType: MappingDisplayTypes = MappingDisplayTypes.FilterAndGrids
): Promise<void> {
    await PageContext.initializeMappingInContainer(container, mappingName, displayType);
}

/**
 * Selector to find DMS Controls or DMS Grids on the current page.
 * @param selector Either string or JQuery-selector or ControlID.
 * @since 4.0.0
 * @returns Either the respective control instance, grid instance or null if no element was found.
 * @example
 * ```JavaScript
 * $dms(1337); // returns control instance with the id 1337.
 * $dms("tst_my_Grid"); // returns grid instance of the grid with the name tst_my_Grid.
 * $dms(".dms-grid"); // returns grid instance of first grid on this page.
 * ```
 */
export function $dms(selector: string | JQuery | HTMLElement | number): IDMSElement {
    if (selector != null) {
        if (typeof selector === "string") {
            // String
            let gridName = selector;

            let grids = PageContext.getGrids();
            for (let i = 0, len = grids.length; i < len; i++) {
                if (grids[i].getGridName() == gridName) {
                    return grids[i];
                }
            }
            return $dms($(selector)[0]);
        } else if (typeof selector === "number") {
            let controlId = selector;

            let grids = PageContext.getGrids();
            for (let i = 0, len = grids.length; i < len; i++) {
                let control = grids[i].findControl(controlId);
                if (control != null) {
                    return control;
                }
            }
            return null;
        } else if ((<JQuery>selector).jquery && (<JQuery>selector).length) {
            // JQuery
            let jquerySelector = <JQuery>selector;
            if (jquerySelector.length > 0) {
                return $dms(jquerySelector[0]);
            }
        } else {
            // HTMLElement
            let domSelector: HTMLElement = <HTMLElement>selector;

            let grids = PageContext.getGrids();
            let gridsArrLength = grids.length;

            for (let i = 0, len = grids.length; i < len; i++) {
                let control = grids[i].findControl(domSelector);
                if (control != null) {
                    return control;
                }
            }

            // loop through all grids.
            for (let i = 0; i < gridsArrLength; i++) {
                if (grids[i].getElement() == domSelector) {
                    // compare dom elements
                    return grids[i];
                }
            }
        }
    }
    return null;
}
